<script setup lang="ts">
import type { CatalogTemplate } from '~/store/billing'
import type { ClientImageOption } from '~/composables/useCheckoutConfigurator'
import type { TemplateCardView, TemplateShelfFilter, TemplateSpecs } from '~/utils/template-catalog'

type TemplateSource = 'os' | 'marketplace' | 'byo'

/**
 * Choosing what a server runs: an operating system, a marketplace app, or one of
 * the customer's own images. ONE component for the checkout (both shells) and the
 * reinstall modal, so the two cannot drift again.
 *
 * It was built for a dozen apps and the catalog now holds 105. Every tile was
 * equal, said nothing about what an app does, sat in a box that scrolled inside
 * the page, and the search looked only at the open tab. What replaced it:
 *
 *  - the choice itself first (`TemplatePickerSelected`): what was picked, how to
 *    get in, and whether the plan runs it;
 *  - one search across operating systems AND apps, by name or by purpose, in the
 *    customer's language, forgiving accents, punctuation and typos
 *    (`~/utils/template-search`);
 *  - the marketplace opening on a curated few, with shelves one click away and
 *    every app described in a sentence (`TemplatePickerApps`);
 *  - operating systems in Linux / Windows / other sections (`TemplatePickerOs`).
 *
 * Its contract is unchanged: `v-model` (the code), `v-model:source` (which tab),
 * `v-model:client-image-id`, and `remedy` when a shortfall's button is pressed.
 * Everything a card says is resolved here, once per template, into a
 * `TemplateCardView` — so a card, a search result and the summary cannot say
 * different things about the same image.
 */
const props = withDefaults(defineProps<{
  source: TemplateSource
  modelValue: string | null
  templates: CatalogTemplate[]
  /**
   * The configuration the image has to run on, so each can say what it needs
   * BEFORE it is picked. All optional: the reinstall flow knows the instance's
   * specs, and a checkout that has not chosen a plan yet — the normal case, now
   * that the image comes first — knows none of them.
   */
  planVcpu?: number | null
  planRamMb?: number | null
  /** Total main disk, in GB. Extra data disks never hold the OS. */
  mainDiskGb?: number | null
  /**
   * Whether the flow can still grow the main disk. Checkout can, and its storage
   * step announces the raise, so the picker says nothing about disk there. A
   * reinstall cannot, so a disk minimum becomes a requirement it genuinely fails.
   */
  diskAdjustable?: boolean
  /** Label for the button offered beside an unmet requirement; omit for none. */
  remedyLabel?: string
  /** What to do instead, for a flow whose remedy is not a button here. */
  remedyHint?: string
  /** Whether the "my images" tab is on offer (a reinstall has its own flow). */
  allowByo?: boolean
  /** The customer's own ready disk images, for the "my images" tab. */
  clientImages?: ClientImageOption[]
  /** Which of those is selected, or null for "blank instance". */
  clientImageId?: string | null
  /** Which flow this is, for analytics. */
  surface?: 'checkout' | 'reinstall'
}>(), {
  // `withDefaults`, not bare optional booleans: Vue casts an ABSENT Boolean prop
  // to `false`, which once dropped the "own image" tab from the order flow.
  allowByo: true,
  diskAdjustable: true,
  clientImages: () => [],
  clientImageId: null,
  surface: 'checkout',
})

const emit = defineEmits<{
  'update:source': [value: TemplateSource]
  'update:modelValue': [value: string | null]
  /** The chosen image needs more than the current configuration provides. */
  'remedy': []
  /** A customer image was chosen, or cleared back to a blank instance. */
  'update:clientImageId': [value: string | null]
}>()

const { t, te } = useI18n()
const { trackSearch, trackSelectItem } = useAnalytics()

const panelId = useId()
const tabIdPrefix = useId()
const query = ref('')
const shelf = ref<TemplateShelfFilter>('featured')
/** Whether the customer has moved between shelves themselves (see below). */
let shelfChosen = false
function setShelf(value: TemplateShelfFilter) {
  shelfChosen = true
  shelf.value = value
}
/**
 * One clock for what counts as new, carried in the SSR payload: a render and its
 * hydration a second apart must not disagree about a badge at the 60-day line.
 */
const now = useState('template-picker-now', () => Date.now())

const specs = computed<TemplateSpecs>(() => ({
  vcpu: props.planVcpu ?? null,
  ramMb: props.planRamMb ?? null,
  mainDiskGb: props.mainDiskGb ?? null,
  diskAdjustable: props.diskAdjustable,
}))
const planKnown = computed(() => specs.value.vcpu != null || specs.value.ramMb != null)

const osTemplates = computed(() =>
  props.templates.filter((template) => template.template_type === 'OPERATING_SYSTEM')
)

// The release chosen for each distribution during this visit, which a tile
// restores when it is clicked again. Kept HERE, not in the OS view: a search or
// another tab takes that view off the page, and a tile that forgot 22.04 after
// a search reads as broken.
const releaseByFamily = reactive<Record<string, string>>({})
watch(
  () => props.modelValue,
  (code) => {
    const template = osTemplates.value.find((candidate) => candidate.code === code)
    if (template) releaseByFamily[template.category] = template.code
  },
  { immediate: true }
)
const appTemplates = computed(() =>
  props.templates.filter((template) => template.template_type === 'MARKETPLACE_APP')
)

/**
 * The copy for a template, from the locale files: the platform is whitelabel and
 * the API never finishes a sentence a customer reads. An app nobody has written
 * a line for yet (one published this morning in the Super Admin Console) gets a
 * generic sentence, never a blank one.
 */
function copyFor(template: CatalogTemplate): { tagline: string; keywords: string } {
  const isApp = template.template_type === 'MARKETPLACE_APP'
  const base = isApp
    ? `billing.page.checkout.os.catalog.apps.${catalogCopyKey(template.code)}`
    : `billing.page.checkout.os.catalog.families.${osFamilySlug(template.category)}`
  const tagline = te(`${base}.tagline`)
    ? t(`${base}.tagline`)
    : isApp
      ? t('billing.page.checkout.os.catalog.genericTagline')
      : ''
  const keywords = te(`${base}.keywords`) ? t(`${base}.keywords`) : ''
  return { tagline, keywords }
}

const views = computed(() => {
  const map = new Map<string, TemplateCardView>()
  for (const template of props.templates) {
    const isApp = template.template_type === 'MARKETPLACE_APP'
    const shelfKey = isApp ? templateShelf(template) : null
    const { tagline, keywords } = copyFor(template)
    const shortfall = templateFit(template, specs.value)
    const minimum = planKnown.value
      ? null
      : specLabel({
          vcpu: template.min_vcpu,
          ramMb: template.min_ram_mb,
          diskGb: specs.value.diskAdjustable ? null : template.min_disk_gb,
        }) || null
    map.set(template.code, {
      code: template.code,
      template,
      shelf: shelfKey,
      groupLabel: shelfKey
        ? t(`billing.page.checkout.os.marketplaceCategories.${shelfKey}`)
        : template.category,
      tagline,
      keywords,
      version: isApp ? appVersionLabel(template) : template.version_label || null,
      isNew: isNewTemplate(template, now.value),
      shortfall,
      minimum,
      fallbackIcon: fallbackIcon(template),
      accent: TEMPLATE_SHELF_STYLE[shelfKey ?? 'other'].accent,
    })
  }
  return map
})

const appViews = computed(() =>
  appTemplates.value.map((template) => views.value.get(template.code)!).filter(Boolean)
)

/**
 * An app chosen from OUTSIDE the shelves — a link from the website, a restored
 * draft, a search result — opens on its own shelf when it is not among the
 * featured ones, so the checked card sits among its neighbours instead of
 * existing only in the summary above. Only until the customer picks a shelf:
 * after that, the shelf is theirs and never jumps under them.
 */
watch(
  () => [props.source, props.modelValue, appViews.value.length] as const,
  ([source, code]) => {
    if (shelfChosen || source !== 'marketplace' || !code) return
    const own = views.value.get(code)?.shelf
    if (own && featuredRank(code) === Number.POSITIVE_INFINITY) shelf.value = own
  },
  { immediate: true }
)

// ── Search ─────────────────────────────────────────────────────────────────
const searchIndex = computed(() =>
  buildTemplateSearchIndex(
    props.templates.map((template) => {
      const view = views.value.get(template.code)!
      const isApp = template.template_type === 'MARKETPLACE_APP'
      return {
        id: template.code,
        name: template.name,
        code: template.code,
        version: template.version_label,
        // An app's category is its product name ("Kubernetes" for k3s, "VPN"
        // for wg-easy): worth finding it by.
        keywords: isApp ? `${view.keywords} ${template.category}` : view.keywords,
        tagline: view.tagline,
        shelf: view.groupLabel,
        rank: featuredRank(template.code),
      }
    })
  )
)

const trimmedQuery = computed(() => query.value.trim())
const hits = computed(() =>
  trimmedQuery.value ? searchTemplateIndex(searchIndex.value, trimmedQuery.value) : []
)
const hitViews = computed(() => hits.value.map((hit) => views.value.get(hit.id)).filter((view): view is TemplateCardView => !!view))
const hitApps = computed(() => hitViews.value.filter((view) => view.template.template_type === 'MARKETPLACE_APP'))
const hitSystems = computed(() => hitViews.value.filter((view) => view.template.template_type === 'OPERATING_SYSTEM'))

/** A one-letter query matches most of the catalog; show a page of it at a time. */
const RESULT_PAGE = 24
const resultLimit = ref(RESULT_PAGE)
watch(trimmedQuery, () => {
  resultLimit.value = RESULT_PAGE
})
const shownApps = computed(() => hitApps.value.slice(0, resultLimit.value))

const docker = computed(() => views.value.get('docker-ce') ?? null)

/**
 * One event per settled query, and never the same one twice in a row: a
 * customer pausing mid-word is not two searches.
 */
let lastReported = ''
const reportSearch = useDebounceFn((term: string, count: number) => {
  if (term.length < 2 || term === lastReported) return
  lastReported = term
  trackSearch(term, count, props.surface)
}, 1000)
watch(trimmedQuery, (term) => {
  if (term) reportSearch(term, hits.value.length)
})

// ── Selection ──────────────────────────────────────────────────────────────
const selectedView = computed(() =>
  props.source === 'byo' || !props.modelValue ? null : views.value.get(props.modelValue) ?? null
)

function report(code: string, listId: string) {
  const template = views.value.get(code)?.template
  if (!template) return
  trackSelectItem(
    { item_id: template.code, item_name: template.name, item_category: template.template_type },
    listId
  )
}

/**
 * Choose an image, from whichever view offered it. The SOURCE goes first: the
 * checkout's `selectedTemplateCode` setter writes to whichever tab is open, so
 * a code emitted before its tab would be filed where nothing reads it.
 */
function selectCode(code: string, listId: string) {
  const template = views.value.get(code)?.template
  if (!template) return
  const source = template.template_type === 'MARKETPLACE_APP' ? 'marketplace' : 'os'
  if (source !== props.source) emit('update:source', source)
  emit('update:modelValue', code)
  report(code, listId)
}

function selectOs(code: string) {
  emit('update:modelValue', code)
  report(code, 'os')
}

function selectSource(source: TemplateSource) {
  if (source === props.source) return
  query.value = ''
  emit('update:source', source)
}

function browseAll() {
  query.value = ''
  setShelf('all')
  if (props.source !== 'marketplace') emit('update:source', 'marketplace')
}

const counts = computed(() => ({
  families: new Set(osTemplates.value.map((template) => template.category)).size,
  versions: osTemplates.value.length,
  apps: appTemplates.value.length,
  images: props.clientImages.length,
}))
</script>

<template>
  <!-- The picker lays itself out by its OWN width (container queries): two
       columns in the checkout's step, three or four in the wide reinstall
       modal, one on a phone. -->
  <div class="@container space-y-4">
    <CheckoutTemplatePickerSelected
      v-if="selectedView"
      :view="selectedView"
      :kind="selectedView.template.template_type === 'MARKETPLACE_APP' ? 'app' : 'os'"
      :specs="specs"
      :remedy-label="remedyLabel"
      :remedy-hint="remedyHint"
      @remedy="emit('remedy')"
    />

    <CheckoutTemplatePickerTabs
      :source="source"
      :allow-byo="allowByo"
      :counts="counts"
      :panel-id="panelId"
      :tab-id-prefix="tabIdPrefix"
      @select="selectSource"
    />

    <div
      :id="panelId"
      role="tabpanel"
      :aria-labelledby="`${tabIdPrefix}-${source}`"
      class="space-y-4"
    >
      <CheckoutTemplatePickerByo
        v-if="source === 'byo'"
        :images="clientImages"
        :image-id="clientImageId"
        @update:image-id="emit('update:clientImageId', $event)"
      />

      <template v-else>
        <CheckoutTemplatePickerSearch
          v-model="query"
          :source="source"
          :result-count="trimmedQuery ? hits.length : null"
        />

        <template v-if="trimmedQuery">
          <CheckoutTemplatePickerResults
            :apps="shownApps"
            :systems="hitSystems"
            :source="source"
            :query="trimmedQuery"
            :model-value="modelValue"
            :docker="docker"
            @select="(code) => selectCode(code, 'search')"
            @clear="query = ''"
            @browse-all="browseAll"
          />
          <div v-if="hitApps.length > shownApps.length" class="flex justify-center">
            <UButton color="neutral" variant="outline" size="sm" @click="resultLimit += RESULT_PAGE">
              {{ $t('billing.page.checkout.os.catalog.search.showMore', { count: hitApps.length - shownApps.length }) }}
            </UButton>
          </div>
        </template>

        <CheckoutTemplatePickerApps
          v-else-if="source === 'marketplace'"
          :shelf="shelf"
          @update:shelf="setShelf"
          :views="appViews"
          :model-value="modelValue"
          @select="selectCode"
        />

        <CheckoutTemplatePickerOs
          v-else
          :templates="osTemplates"
          :model-value="modelValue"
          :specs="specs"
          :remembered="releaseByFamily"
          @update:model-value="selectOs"
        />
      </template>
    </div>
  </div>
</template>
